import React, { useEffect, useState, useRef, Children } from 'react';
import { PlayIcon, PauseIcon, MenuIcon, XIcon } from 'lucide-react';
export function BlogPost() {
  const [activeSection, setActiveSection] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState('1');
  const [showBubble, setShowBubble] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const speechSynthesisRef = useRef(window.speechSynthesis);
  const utteranceRef = useRef(null);
  useEffect(() => {
    // Create utterance for narration
    utteranceRef.current = new SpeechSynthesisUtterance();
    // Clean up function
    return () => {
      if (speechSynthesisRef.current.speaking) {
        speechSynthesisRef.current.cancel();
      }
    };
  }, []);
  useEffect(() => {
    if (utteranceRef.current) {
      utteranceRef.current.rate = parseFloat(playbackSpeed);
    }
  }, [playbackSpeed]);
  const toggleNarration = () => {
    if (!isPlaying) {
      // Start narration
      const textToRead = document.querySelector('article h1, article p, article ul, article ol, article h2, article h3').textContent;
      utteranceRef.current.text = textToRead;
      utteranceRef.current.rate = parseFloat(playbackSpeed);
      utteranceRef.current.onend = () => setIsPlaying(false);
      speechSynthesisRef.current.speak(utteranceRef.current);
      setIsPlaying(true);
    } else {
      // Pause narration
      speechSynthesisRef.current.cancel();
      setIsPlaying(false);
    }
  };
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
          // If the illustration section is visible, show the bubble
          if (entry.target.classList.contains('illustration-section')) {
            setShowBubble(true);
          }
        }
      });
    }, {
      threshold: 0.2
    });
    document.querySelectorAll('h2[id], .illustration-section').forEach(section => {
      observer.observe(section);
    });
    return () => observer.disconnect();
  }, []);
  return <div className="relative lg:flex lg:gap-8" data-id="element-5">
      {/* Mobile Menu Button */}
      <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="fixed top-4 right-4 z-50 p-2 rounded-lg bg-white dark:bg-gray-800 shadow-lg lg:hidden" data-id="element-6">
        {isMobileMenuOpen ? <XIcon className="h-6 w-6" data-id="element-7" /> : <MenuIcon className="h-6 w-6" data-id="element-8" />}
      </button>
      {/* Sidebar Navigation - Modified for mobile */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-36 transform transition-transform duration-300 ease-in-out lg:relative lg:transform-none lg:transition-none ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} lg:block lg:sticky lg:top-8 lg:h-screen overflow-y-auto pb-8`} data-id="element-9">
        <nav className="bg-white dark:bg-gray-900 h-full rounded-lg p-1.5 shadow-lg lg:shadow-none" data-id="element-10">
          {/* Close button for mobile */}
          <div className="flex justify-between items-center mb-1.5 lg:hidden" data-id="element-11">
            <h2 className="font-bold text-sm" data-id="element-12">Navigation</h2>
          </div>
          <ol className="space-y-0.5" data-id="element-13">
            {[{
            id: 'coordination-problem',
            title: 'How We Got Stuck in This Trap'
          }, {
            id: 'building-instead',
            title: "What We're Building Instead"
          }, {
            id: 'nomad-border-pass',
            title: 'The Nomad Border Pass'
          }, {
            id: 'why-building-this-is-hard',
            title: 'Why Building This Is Hard'
          }, {
            id: 'network-effects',
            title: 'The Network Effects You Actually Want'
          }, {
            id: 'selection-criteria',
            title: 'Selection Criteria and Process'
          }, {
            id: 'pricing',
            title: 'Why It Costs What It Costs'
          }, {
            id: 'getting-started',
            title: 'Getting Started'
          }, {
            id: 'where-this-could-lead',
            title: 'Where This Could Lead'
          }].map(({
            id,
            title
          }, index) => <li key={id} data-id="element-14">
                <a href={`#${id}`} className={`block py-2 px-3 rounded-md transition-all duration-200 break-words whitespace-normal overflow-wrap-anywhere text-sm ${activeSection === id ? 'bg-gray-100 dark:bg-gray-900 text-blue-600 dark:text-blue-400 shadow-sm' : 'hover:bg-gray-100 dark:hover:bg-gray-900 hover:shadow-sm'}`} data-id="element-15">
                  {title}
                </a>
              </li>)}
          </ol>
        </nav>
      </aside>
      {/* Overlay for mobile */}
      {isMobileMenuOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden" onClick={() => setIsMobileMenuOpen(false)} data-id="element-16" />}
      {/* Main Content */}
      <article className="prose dark:prose-invert max-w-none lg:max-w-2xl mx-auto px-4 prose-p:leading-relaxed prose-p:font-serif prose-headings:font-sans prose-li:leading-relaxed prose-li:font-serif" data-id="element-17">
        <h1 className="text-3xl sm:text-4xl font-bold mb-4 sm:mb-6 pb-2 sm:pb-3 border-b border-gray-200 dark:border-gray-700 scroll-mt-20" data-id="element-18">
          Nomad Citizen: A Systems Approach to Post-Geographic Citizenship
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6" data-id="element-19">
          Last Updated: 2025 August 5
        </p>
        <h2 id="executive-summary" className="text-2xl sm:text-3xl font-bold mt-6 sm:mt-8 mb-3 sm:mb-4 scroll-mt-20" data-id="element-20">
          Executive Summary
        </h2>
        <p data-id="element-21">
          Imagine you're a software engineer earning $150K remotely. You split
          time between three months in Portugal, two in Thailand, and a stint in
          Argentina. By every reasonable metric, you're{' '}
          <strong data-id="element-22">
            <em data-id="element-23">exactly</em>
          </strong>{' '}
          the kind of person the global economy should be optimized for.
        </p>
        <p data-id="element-24">
          <strong data-id="element-25">
            Instead, you've discovered a systematic flaw in digital nomadism.
          </strong>{' '}
          We've built a world optimized for people who live in one place, work
          for one employer, and have one address. But if you're reading this,
          you've probably already discovered that{' '}
          <em data-id="element-26">reality is more complex</em>. Try explaining to a Portuguese
          emergency room that your health insurance is American, your income
          flows through Singapore, and your "permanent address" is your parents'
          house in Ohio.
        </p>
        <p data-id="element-27">
          Nobody designed this system to be hostile to location independence -
          it emerged from decades of institutions optimizing locally while the
          global experience became increasingly kafkaesque. Nomad Citizen solves
          this trap through coordinated global services: combining health
          coverage, visa access, and a social infrastructure into a coherent
          system designed for post-geographic lifestyles.
        </p>
        <p data-id="element-28">
          <strong data-id="element-29">
            We're explicitly starting with <em data-id="element-30">50-100</em> founding members
          </strong>{' '}
          because building a new global infrastructure requires intimate scale
          and direct relationships.
        </p>
        <div className="bg-white dark:bg-gray-800/50 rounded-xl p-6 my-8 shadow-lg border border-gray-200 dark:border-gray-700" data-id="element-31">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4" data-id="element-32">
            Table of Contents
          </h2>
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-2" data-id="element-33">
            {[{
            id: 'coordination-problem',
            title: 'How We Got Stuck in This Trap'
          }, {
            id: 'building-instead',
            title: "What We're Building Instead"
          }, {
            id: 'nomad-border-pass',
            title: 'The Nomad Border Pass'
          }, {
            id: 'why-building-this-is-hard',
            title: 'Why Building This Is Hard'
          }, {
            id: 'network-effects',
            title: 'The Network Effects You Actually Want'
          }, {
            id: 'selection-criteria',
            title: 'Selection Criteria and Process'
          }, {
            id: 'pricing',
            title: 'Why It Costs What It Costs'
          }, {
            id: 'getting-started',
            title: 'Getting Started'
          }, {
            id: 'where-this-could-lead',
            title: 'Where This Could Lead'
          }].map(({
            id,
            title
          }, index) => <li key={id} data-id="element-34">
                <a href={`#${id}`} className={`block py-2 px-3 rounded-md transition-all duration-200 break-words whitespace-normal overflow-wrap-anywhere text-sm ${activeSection === id ? 'bg-gray-100 dark:bg-gray-900 text-blue-600 dark:text-blue-400 shadow-sm' : 'hover:bg-gray-100 dark:hover:bg-gray-900 hover:shadow-sm'}`} data-id="element-35">
                  {title}
                </a>
              </li>)}
          </ol>
        </div>
        <div className="relative illustration-section my-4 sm:my-6" data-id="element-36">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/7Ja1KSpp1c8bawYj2ajBZo/Screenshot_2025-06-14_at_4.37.52_AM.png" alt="Illustration of a futuristic eco-city with sustainable architecture, featuring pyramidal buildings, geodesic domes, and a winding river, all under a large Earth in the sky" className="w-full rounded-lg my-6 sm:my-8 shadow-lg" data-id="element-37" />
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/c3GbXk4WTpANTii8tHgF8P/speech_bubble1.png" alt="Speech bubble saying: heard the internet's getting a country" className={`absolute -right-2 sm:-right-4 top-1/4 transform sm:transform sm:-translate-y-1/2 w-36 sm:w-48 md:w-64 lg:w-72 transition-all duration-500 ${showBubble ? 'opacity-100 translate-x-0' : 'opacity-0 sm:translate-x-12 translate-x-8'}`} data-id="element-38" />
        </div>
        <h2 id="coordination-problem" className="text-2xl sm:text-3xl font-bold mt-6 sm:mt-8 mb-3 sm:mb-4 scroll-mt-20" data-id="element-39">
          How We Got Stuck in This Trap
        </h2>
        <p data-id="element-40">
          The fundamental problem is that institutions built a world that no
          longer exists for everybody, and nobody updated them for 2025.
        </p>
        <p data-id="element-41">
          <strong data-id="element-42">The insurance model (broken):</strong> Insurance companies
          built their entire business model around employer relationships. Why?
          Because it worked in 1960 when people stayed at one company for thirty
          years. But now you're a consultant working for three different
          startups while based in four different countries, and the insurance
          company's computer system literally cannot process your existence.
        </p>
        <p data-id="element-43">
          <strong data-id="element-44">The nation state model (broken):</strong> Countries designed
          their systems around the assumption that "residents" and "taxpayers"
          were basically the same category. If you lived in France, you worked
          in France, you paid taxes in France, you used French hospitals. The
          idea that someone might earn money in Singapore, live in Portugal, and
          need emergency care in Thailand doesn't exist in their mental model.
        </p>
        <p data-id="element-45">
          <strong data-id="element-46">The banking model (broken):</strong> Banks optimized for
          address verification because that's how they prevented fraud in 1995.
          But now you're trying to open an account and they want proof of
          residence, except you don't really "reside" anywhere for more than
          three months, despite being a very real person. According to the
          customer service representative who just wants to check a box that
          says "PROOF OF ADDRESS: YES/NO" though, you apparently aren't.
        </p>
        <p data-id="element-47">
          Each institution, optimized locally, created a system that's globally
          hostile to the exact people who could be its most valuable members.
        </p>
        <h2 id="building-instead" className="text-2xl sm:text-3xl font-bold mt-6 sm:mt-8 mb-3 sm:mb-4 scroll-mt-20" data-id="element-48">
          What We're Building Instead
        </h2>
        <p data-id="element-49">
          Nomad Citizen is infrastructure for people who work from everywhere,
          built by people who understand that geographic assumptions are
          increasingly fiction. Instead of trying to fix the existing system
          (impossible), <strong data-id="element-50">we built a new one</strong>.
        </p>
        <p data-id="element-51">
          This isn't just "we'll cover you if you get sick abroad." This is what
          happens when you realize that medical care is a global market, but
          most insurance companies still think the best treatment available to
          you is whatever happens to be within driving distance of your ZIP
          code.
        </p>
        <h3 className="text-xl sm:text-2xl font-bold mt-4 sm:mt-6 mb-2 sm:mb-3" data-id="element-52">
          Health Coverage That Follows You: $1.5M Annual Limit
        </h3>
        <p data-id="element-53">
          Remember trying to explain to that Portuguese emergency room why your
          American insurance should cover you? We solve this by building
          coverage that actually works globally.
        </p>
        <ul className="list-disc pl-4 sm:pl-6 space-y-1 sm:space-y-1.5 text-sm sm:text-base" data-id="element-54">
          <li data-id="element-55">
            Hospital coverage: Unlimited globally with 24/7 white glove
            concierge service
          </li>
          <li data-id="element-56">Outpatient coverage: $5,000 annually</li>
          <li data-id="element-57">Emergency transport: $100K for air/ground/sea ambulance</li>
          <li data-id="element-58">
            <span className="bg-yellow-100 dark:bg-yellow-900/30" data-id="element-59">
              Wellness therapies*: 30 visits annually including massage and
              psychiatric care - because nomad life breaks both your body and
              your brain
            </span>
          </li>
        </ul>
        <h3 className="text-xl sm:text-2xl font-bold mt-4 sm:mt-6 mb-2 sm:mb-3" data-id="element-60">
          Income Protection That Makes Sense
        </h3>
        <p data-id="element-61">
          We built reliable income protection for people whose office changes
          time zones monthly.
        </p>
        <ul className="list-disc pl-4 sm:pl-6 space-y-1 sm:space-y-1.5 text-sm sm:text-base" data-id="element-62">
          <li data-id="element-63">
            Job loss: $4,000/month for 6 months (full-time) or 3 months
            (free-lance/consulting)
          </li>
          <li data-id="element-64">Medical leave: $4,000/month if you can't work due to illness</li>
          <li data-id="element-65">
            Parental leave: $4,000/month for 4 months (birthing parent) or 2
            months (partner)
          </li>
          <li data-id="element-66">
            Critical illness: $4,000/month until age 60 if you develop something
            serious
          </li>
          <li data-id="element-67">
            Long term care: $4,000/month from ages 61-75 if you need ongoing
            assistance
          </li>
        </ul>
        <h3 className="text-xl sm:text-2xl font-bold mt-4 sm:mt-6 mb-2 sm:mb-3" data-id="element-68">
          Travel Protection That Isn't Insulting
        </h3>
        <p data-id="element-69">
          Travel insurance usually covers lost luggage but not the two weeks you
          spend replacing everything. We cover the actual problems:
        </p>
        <ul className="list-disc pl-4 sm:pl-6 space-y-1 sm:space-y-1.5 text-sm sm:text-base" data-id="element-70">
          <li data-id="element-71">
            Lost luggage: $500/item, $3,000/event (enough to actually replace
            your laptop and camera)
          </li>
          <li data-id="element-72">
            Emergency evacuation: $10K if you need to leave somewhere fast
          </li>
          <li data-id="element-73">
            Security incidents: $10K if you're robbed under duress (yes, this
            happens)
          </li>
          <li data-id="element-74">Trip interruption: $5,000 for emergency flights home</li>
        </ul>
        <h3 className="text-xl sm:text-2xl font-bold mt-4 sm:mt-6 mb-2 sm:mb-3" data-id="element-75">
          Life Insurance for People Who Live Everywhere
        </h3>
        <ul className="list-disc pl-4 sm:pl-6 space-y-1.5 sm:space-y-2 text-sm sm:text-base" data-id="element-76">
          <li data-id="element-77">
            Accidental death: $50,000 to beneficiaries ($25,000 for children)
          </li>
          <li data-id="element-78">Term life: $4,000/month to beneficiaries for 3 years</li>
          <li data-id="element-79">
            Repatriation: $20K to get your body home, $10K for local arrangement
          </li>
        </ul>
        <h2 id="nomad-border-pass" className="text-2xl sm:text-3xl font-bold mt-6 sm:mt-8 mb-3 sm:mb-4 scroll-mt-20" data-id="element-80">
          The Nomad Border Pass
        </h2>
        <p data-id="element-81">
          <strong data-id="element-82">Nomad Border Pass (Summer 2025)</strong> is our attempt to
          escape what we call the <strong data-id="element-83">'Visa Trap'</strong> - that
          mind-bending system where governments want qualified applicants but
          drown them in contradictory information from embassy websites last
          updated in 2019.
        </p>
        <p data-id="element-84">
          Every nomad knows the drill: You want to spend six months in
          Argentina, so you GPT "Argentina digital nomad visa" and find
          seventeen different blog posts, an official government website that
          contradicts itself, and a Facebook group where people share
          screenshots of emails from embassy officials.
        </p>
        <p data-id="element-85">
          The result: talented people spending weeks deciphering whether "proof
          of accommodation" means a hotel booking, a lease, or a letter from
          their Airbnb host.
        </p>
        <p className="font-bold mt-4 mb-2" data-id="element-86">7 Jurisdictions (coming soon):</p>
        <ul className="list-none pl-4 sm:pl-6 space-y-2" data-id="element-87">
          <li data-id="element-88">🇹🇭 Thai DTV</li>
          <li data-id="element-89">🇦🇷 Argentina Digital NoMad Visa</li>
          <li data-id="element-90">🇨🇴 Colombia Nómad Digital</li>
          <li data-id="element-91">🇭🇷 Croatia Digital NoMad Permit</li>
          <li data-id="element-92">🇨🇷 Costa Rica Rentista Visa</li>
          <li data-id="element-93">🇲🇹 Malta NoMad Residence Permit</li>
          <li data-id="element-94">🇹🇼 Taiwan Employment Gold Card</li>
        </ul>
        <p className="mt-4" data-id="element-95">
          <strong data-id="element-96">How It Works:</strong> Instead of you becoming an expert in
          seven different bureaucracies, we handle the coordination. We research
          requirements, compile documentation, submit applications, and follow
          up with government officials.
        </p>
        <p data-id="element-97">
          Our team includes experts who specialize in each country's immigration
          system. They understand the unwritten rules, know which documents
          actually matter (versus what's listed on outdated websites), and
          maintain relationships with processing officials. This means you're
          not just getting a service that fills out forms - you're getting
          decades of specialized knowledge that can navigate the subtle
          differences between how Thailand processes applications versus Croatia
          or Argentina.
        </p>
        <p className="mt-4" data-id="element-98">
          <strong data-id="element-99">How It Works:</strong> Instead of you becoming an expert in
          seven different bureaucracies, we handle the coordination. We research
          requirements, compile documentation, submit applications, and follow
          up with government officials.
        </p>
        <h2 id="why-building-this-is-hard" className="text-2xl sm:text-3xl font-bold mt-6 sm:mt-8 mb-2 sm:mb-3 scroll-mt-20" data-id="element-100">
          Why Building This Is Hard
        </h2>
        <p className="mt-4" data-id="element-101">
          <strong data-id="element-102">24/7 Global Operations Reality</strong> When you need
          emergency help, it's never during business hours in the right time
          zone.
        </p>
        <p data-id="element-103">
          <i data-id="element-104">Why this matters:</i> Real global coverage requires extensive
          operational competency that most companies can't justify building. We
          built it because anything less doesn't actually work when you need it.
        </p>
        <p className="mt-4" data-id="element-105">
          <strong data-id="element-106">Government Processes</strong> We can research requirements,
          compile documentation, follow up with officials, but we can't make
          bureaucracies rational or timelines predictable. Portuguese visa
          processing doesn't speed up because you need it faster.
        </p>
        <p data-id="element-107">
          <i data-id="element-108">Why this matters:</i> Anyone promising to "guarantee" visa
          approvals is lying to you. We eliminate the research burden and handle
          the coordination, but we can't magic away the fact that governments
          are going to government.
        </p>
        <p className="mt-4" data-id="element-109">
          <strong data-id="element-110">Building Infrastructure, Not Products</strong> Most companies
          optimize for scale from day one. We're optimizing for getting the
          model right first. Managing global provider networks and 24/7
          logistics across time zones requires operational depth that only comes
          from direct experience.
        </p>
        <p data-id="element-111">
          <i data-id="element-112">Why this matters:</i> founding cohort gets access to portable
          infrastructure that couldn't exist at mass scale. This level of white
          glove logistics is only possible when we know every member personally.
        </p>
        <h2 id="network-effects" className="text-2xl sm:text-3xl font-bold mt-6 sm:mt-8 mb-2 sm:mb-3 scroll-mt-20" data-id="element-113">
          The Network Effects You Actually Want
        </h2>
        <p data-id="element-114">
          Beyond insurance and visas, you get access to mechanisms that only
          work at intimate scale:
        </p>
        <p data-id="element-115">
          <strong data-id="element-116">Community That Creates Value</strong>: Regional member-only
          events (
          <a href="https://plumia.org/" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 no-underline" data-id="element-117">
            Plumia dinner series
          </a>{' '}
          in major cities) to connect with other high value individuals who've
          navigated similar lifestyle choices.
        </p>
        <ul className="list-disc pl-4 sm:pl-6 space-y-1 sm:space-y-1.5 text-sm sm:text-base" data-id="element-118"></ul>
        <p data-id="element-119">
          <strong data-id="element-120">Trust Networks That Scale</strong>: The small initial cohort
          enables higher trust - the kind of network where someone vouches for
          you when you need an intro, or puts you in touch with their accountant
          who understands international tax situations.
        </p>
        <h2 id="selection-criteria" className="text-2xl sm:text-3xl font-bold mt-6 sm:mt-8 mb-2 sm:mb-3 scroll-mt-20" data-id="element-121">
          Selection Criteria and Process
        </h2>
        <p data-id="element-122">We're looking for founding members who:</p>
        <ul className="list-disc pl-4 sm:pl-6 space-y-2 mt-2 mb-4" data-id="element-123">
          <li data-id="element-124">
            Have demonstrated commitment to location independence (not just
            thinking about it)
          </li>
          <li data-id="element-125">Represent diverse professional backgrounds and nationalities</li>
          <li data-id="element-126">Can provide feedback and help iterate on what we're building</li>
        </ul>
        <p data-id="element-127">
          <strong data-id="element-128">Selection Process:</strong> Rather than first come first
          served, we're being systematic about hand picking our founding cohort
          because coordinating global infrastructure requires intimate
          relationships and tight feedback loops. We want people who see this as
          infrastructure investment.
        </p>
        <h2 id="pricing" className="text-2xl sm:text-3xl font-bold mt-6 sm:mt-8 mb-2 sm:mb-3" data-id="element-129">
          Why This Costs What It Costs
        </h2>
        <p className="font-bold mb-2" data-id="element-130">Pricing Structure (Monthly):</p>
        <ul className="list-disc pl-4 sm:pl-6 space-y-1.5 sm:space-y-2 text-sm sm:text-base" data-id="element-131">
          <li data-id="element-132">Ages 18-39: $400/month</li>
          <li data-id="element-133">Ages 40-49: $600/month</li>
          <li data-id="element-134">Child: $133/month</li>
        </ul>
        <p className="font-bold mt-4 mb-2" data-id="element-135">Selection Criteria (Pilot Phase):</p>
        <ul className="list-disc pl-4 sm:pl-6 space-y-1.5 sm:space-y-2 text-sm sm:text-base" data-id="element-136">
          <li data-id="element-137">Age: Under 50 years</li>
          <li data-id="element-138">
            Income: Minimum $4,000/month verified annually (employment or
            self-employment)
          </li>
          <li data-id="element-139">Mobility: Plan to spend 6+ months outside passport country</li>
          <li data-id="element-140">Health: Basic medical screening (2-3 questions)</li>
        </ul>
        <p className="mt-4" data-id="element-141">
          <strong data-id="element-142">Why These Numbers Make Sense:</strong> Because it doesn't
          exist anywhere else. Try buying global infrastructure that works in
          195 countries, covers liveable income when you lose your job, handles
          visa coordination, provides 24/7 white glove support, AND throws in 30
          massages or psychologist sessions a year - you literally can't. We
          built what everyone needs but no one offers.
        </p>
        <h2 id="getting-started" className="text-2xl sm:text-3xl font-bold mt-6 sm:mt-8 mb-2 sm:mb-3" data-id="element-143">
          Getting Started
        </h2>
        <p data-id="element-144">
          <strong data-id="element-145">The Selection Reality:</strong> We're explicitly being
          selective about the founding cohort. If you're looking for a
          traditional insurance product you can buy immediately, Nomad Citizen
          probably isn't the right fit. We're looking for people who want to
          help build something rather than just consume something that already
          exists. If you're interested in participating, the first step is a
          qualification conversation to ensure we're mutually aligned on what
          we're building and why.
        </p>
        <p className="mt-4" data-id="element-146">If we're aligned, you'll get:</p>
        <ol className="list-decimal pl-4 sm:pl-6 space-y-2 text-sm sm:text-base" data-id="element-147">
          <li data-id="element-148">
            Access to our{' '}
            <a href="https://t.me/nomadcitizens" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 no-underline" data-id="element-149">
              Read Only Telegram group
            </a>{' '}
            for Nomad Citizen updates (product development, intake timeline,
            curated news and more)
          </li>
          <li data-id="element-150">
            The full policy documentation (yes, including all the legal fine
            print)
          </li>
          <li data-id="element-151">A link to activate your membership</li>
        </ol>
        <p className="mt-8" data-id="element-152">
          <strong data-id="element-153">Full Policy Documentation:</strong> Available upon acceptance
          into the pilot program (This isn't because we're hiding anything
          terrible - it's because reading insurance policy language is genuinely
          painful and we'd rather save everyone that experience unless there's
          mutual interest.)
        </p>
        <p className="mt-4" data-id="element-154">
          <strong data-id="element-155">Next Steps:</strong>{' '}
          <span className="bg-yellow-100 dark:bg-yellow-900/30" data-id="element-156">
            If you're accepted as a new member, we'll notify you - until then,
            you'll remain on the waiting list which our Membership Committee
            reviews every quarter.
          </span>
        </p>
        <h2 id="where-this-could-lead" className="text-2xl sm:text-3xl font-bold mt-6 sm:mt-8 mb-2 sm:mb-3 scroll-mt-20" data-id="element-157">
          Where This Could Lead
        </h2>
        <p data-id="element-158">
          If this pilot proves successful, there are several potential scaling
          pathways:
        </p>
        <ul className="list-disc pl-4 sm:pl-6 space-y-1.5 sm:space-y-2 text-sm sm:text-base" data-id="element-159">
          <li data-id="element-160">
            <strong data-id="element-161">Horizontal Expansion:</strong> Adding additional services
            (banking, legal coordination, tax planning) to create a more
            comprehensive "global citizenship" bundle.
          </li>
          <li data-id="element-162">
            <strong data-id="element-163">Vertical Integration:</strong> Coverage protocols that
            include experimental and trial based interventions, and location
            independent treatment selection based on quality metrics rather than
            geographic proximity.
          </li>
          <li data-id="element-164">
            <strong data-id="element-165">Country On The Internet Evolution:</strong> Eventually
            evolving towards communities with increasing coordination that
            transcend traditional geographic boundaries.
          </li>
        </ul>
        <p className="mt-4" data-id="element-166">
          However, these remain speculative. The immediate focus is on testing
          whether the basic coordination mechanisms work effectively for a small
          group before considering broader implications.
        </p>
        <hr className="my-8 border-t border-gray-200 dark:border-gray-700" data-id="element-167" />
        <p className="mt-8 text-gray-500 dark:text-gray-400 italic" data-id="element-168">
          This document represents our current thinking as of August 5, 2025.
          Because we're building institutional infrastructure rather than
          selling traditional products, specifications and processes will be
          optimized based on what we learn from the founding cohort.
        </p>
      </article>
    </div>;
}